/**
 * Hippo card actions — AJAX add-to-cart, Quick View, and the multi-variant picker
 * for the product-card-hippo (`.fp-card`) cards.
 *
 * The cards render plain `<button>`s (not forms) because the collection grid lives
 * inside the filter <form> and HTML forbids nested forms. Every add path funnels
 * through addVariant(): POST /cart/add.js, then dispatch the legacy `ajaxProduct:added`
 * event so theme.CartDrawer (assets/theme.js) rebuilds its contents and opens.
 *
 *   single-variant Quick Add → addVariant() straight to cart
 *   multi-variant  Quick Add → openVariantPicker() → choose → addVariant()
 *   Quick View (eye)         → openQuickView() → (optional) choose → addVariant()
 *
 * Quick View / picker markup is fetched per-product via the Section Rendering API
 * (sections/hippo-quick-view.liquid, sections/hippo-variant-picker.liquid) and
 * injected into the overlay hosts from snippets/hippo-card-overlays.liquid.
 *
 * Loaded as a plain deferred script by the sections that render the card. The init
 * guard makes it safe to include from more than one section.
 */
;(function () {
  if (window.__hippoQuickAddInit) return
  window.__hippoQuickAddInit = true

  const ADDED_RESET_MS = 1500
  const ERROR_VISIBLE_MS = 4000
  const PICKER_CLOSE_MS = 760

  /** @type {HTMLElement | null} Element to restore focus to when an overlay closes. */
  let lastFocus = null

  /* ------------------------------------------------------------------ *
   * Shared add-to-cart
   * ------------------------------------------------------------------ */

  /**
   * Shows a translated, transient error message next to the triggering button.
   * @param {HTMLButtonElement | null | undefined} sourceBtn
   */
  function showAddError(sourceBtn) {
    const message = window.theme?.strings?.quickAddError
    if (!sourceBtn || !message) return
    const note = document.createElement('p')
    note.setAttribute('role', 'alert')
    note.className = 'hippo-quick-add__error'
    note.style.cssText = 'margin:6px 0 0;font-size:12px;color:#c0392b;'
    note.textContent = message
    sourceBtn.insertAdjacentElement('afterend', note)
    setTimeout(() => note.remove(), ERROR_VISIBLE_MS)
  }

  /**
   * Adds a variant to the cart and opens the drawer via `ajaxProduct:added`.
   * @param {string | number} variantId
   * @param {number} quantity
   * @param {HTMLButtonElement | null} [sourceBtn] - button to animate / re-enable
   * @returns {Promise<boolean>} resolves true on success
   */
  function addVariant(variantId, quantity, sourceBtn) {
    if (!variantId) return Promise.resolve(false)

    const qty = Number(quantity) || 1
    const cartAddUrl = window.theme?.routes?.cartAdd || '/cart/add.js'

    if (sourceBtn) {
      sourceBtn.disabled = true
      sourceBtn.classList.add('btn--loading')
    }

    return fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id: variantId, quantity: qty }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          // e.g. 422 — sold out / exceeds stock. Surface and bail.
          console.warn('Quick add failed:', data.description || data.message)
          showAddError(sourceBtn)
          return false
        }

        if (sourceBtn) {
          // Burst state (styled via [data-added='true']).
          sourceBtn.dataset.added = 'true'
          setTimeout(() => sourceBtn.removeAttribute('data-added'), ADDED_RESET_MS)
        }

        // Legacy theme.CartDrawer listens for this: rebuilds from /cart.js + opens.
        document.dispatchEvent(new CustomEvent('ajaxProduct:added'))
        return true
      })
      .catch((error) => {
        console.error('Quick add error:', error)
        showAddError(sourceBtn)
        return false
      })
      .finally(() => {
        if (sourceBtn) {
          sourceBtn.disabled = false
          sourceBtn.classList.remove('btn--loading')
        }
      })
  }

  /**
   * Buy Now: checks out ONLY this variant (assets/hippo-buy-now.js swaps the cart,
   * then navigates to /checkout). The button stays in its loading state on success
   * because the page is leaving; on failure it is re-enabled with an error note.
   * @param {string | number} variantId
   * @param {number} quantity
   * @param {HTMLButtonElement} btn
   * @returns {Promise<boolean>}
   */
  function buyNow(variantId, quantity, btn) {
    if (!variantId || !window.hippoBuyNow) return Promise.resolve(false)

    btn.disabled = true
    btn.classList.add('btn--loading')

    return window.hippoBuyNow
      .buy(variantId, Number(quantity) || 1)
      .catch(() => false)
      .then((ok) => {
        if (!ok) {
          btn.disabled = false
          btn.classList.remove('btn--loading')
          showAddError(btn)
        }
        return ok
      })
  }

  /* ------------------------------------------------------------------ *
   * Section Rendering helpers
   * ------------------------------------------------------------------ */

  /**
   * Fetches a render-only section for a product and returns its inner HTML,
   * stripping any `#shopify-section-*` wrapper so injected children stay direct.
   * @param {string} handle
   * @param {string} sectionId
   * @returns {Promise<string>}
   */
  function fetchSection(handle, sectionId) {
    // Keep the locale prefix (e.g. /en/) so the section renders in the active language.
    const root = (window.theme?.routes?.home || '/').replace(/\/$/, '')
    return fetch(`${root}/products/${handle}?section_id=${sectionId}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((r) => r.text())
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html')
        const wrapper = doc.body.querySelector('[id^="shopify-section"]')
        return (wrapper || doc.body).innerHTML
      })
  }

  /* ------------------------------------------------------------------ *
   * Overlay open / close
   * ------------------------------------------------------------------ */

  /** @param {HTMLElement} overlay */
  function openOverlay(overlay) {
    // hidden->visible can't transition in one frame; force a reflow then animate.
    overlay.getBoundingClientRect()
    overlay.classList.add('is-open')
    document.body.style.overflow = 'hidden'
    // Suppress card hover/zoom/quick-add behind the scrim (cursor stays over the card).
    document.documentElement.classList.add('hippo-overlay-open')
    const focusable = overlay.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (focusable instanceof HTMLElement) focusable.focus()
  }

  /** @param {HTMLElement} overlay */
  function closeOverlay(overlay) {
    overlay.classList.remove('is-open')
    document.body.style.overflow = ''
    document.documentElement.classList.remove('hippo-overlay-open')
    if (lastFocus instanceof HTMLElement) lastFocus.focus()
    lastFocus = null
  }

  /** @returns {HTMLElement | null} the currently open qv/vp overlay, if any */
  function openOverlayEl() {
    return /** @type {HTMLElement | null} */ (
      document.querySelector('[data-qv-overlay].is-open, [data-vp-overlay].is-open')
    )
  }

  /* ------------------------------------------------------------------ *
   * Variant resolution (shared by Quick View + Variant Picker)
   * ------------------------------------------------------------------ */

  /**
   * @typedef {object} HippoVariant
   * @property {number} id
   * @property {string[]} options
   * @property {boolean} available
   * @property {number} price
   * @property {string} price_formatted
   * @property {string | null} image
   * @property {string} title
   * @property {number} min
   */

  /**
   * Reads variants JSON injected by snippets/hippo-variants-json.liquid.
   * @param {Element} container
   * @returns {HippoVariant[]}
   */
  function readVariants(container) {
    const script = container.querySelector('[data-variants]')
    if (!script) return []
    try {
      return JSON.parse(script.textContent || '[]')
    } catch (e) {
      console.warn('Hippo: bad variants JSON', e)
      return []
    }
  }

  /**
   * Returns the selected option value per position (1-based -> index 0..).
   * @param {Element} container
   * @returns {string[]}
   */
  function selectedOptions(container) {
    /** @type {string[]} */
    const values = []
    container.querySelectorAll('[data-option-field]').forEach((field) => {
      const pressed = field.querySelector('[aria-pressed="true"][data-option-position]')
      if (pressed instanceof HTMLElement) {
        const pos = Number(pressed.dataset.optionPosition) - 1
        values[pos] = pressed.dataset.value || ''
      }
    })
    return values
  }

  /**
   * Resolves the variant for the current selection and updates price / image / Add.
   * @param {Element} container - the .qv or .vp content root
   * @returns {HippoVariant | null}
   */
  function resolveVariant(container) {
    const variants = readVariants(container)
    const atc = /** @type {HTMLButtonElement | null} */ (
      container.querySelector('[data-qv-atc], [data-vp-atc]')
    )

    // No options to choose (default variant) — keep the rendered variant.
    const fields = container.querySelectorAll('[data-option-field]')
    let variant = null
    if (!fields.length) {
      variant = variants[0] || null
    } else {
      const chosen = selectedOptions(container)
      variant =
        variants.find(
          (v) => v.options.length === chosen.length && v.options.every((o, i) => o === chosen[i])
        ) || null
    }

    // price
    const priceEl = container.querySelector('[data-qv-price], [data-vp-price]')
    if (priceEl && variant) priceEl.textContent = variant.price_formatted

    // image (qv media / vp thumb)
    if (variant && variant.image) {
      const img = container.querySelector('[data-qv-media] img, [data-vp-thumb] img')
      if (img instanceof HTMLImageElement) {
        img.src = variant.image
        img.removeAttribute('srcset')
      }
    }

    // Add + Buy now buttons (Buy now only exists in the variant picker)
    const buy = /** @type {HTMLButtonElement | null} */ (container.querySelector('[data-vp-buy]'))
    ;[atc, buy].forEach((btn) => {
      if (!btn) return
      if (variant && variant.available) {
        btn.disabled = false
        btn.dataset.variantId = String(variant.id)
      } else {
        btn.disabled = true
        btn.removeAttribute('data-variant-id')
      }
    })

    return variant
  }

  /* ------------------------------------------------------------------ *
   * Quick View
   * ------------------------------------------------------------------ */

  /** @param {string} handle */
  function openQuickView(handle) {
    const overlay = /** @type {HTMLElement | null} */ (document.querySelector('[data-qv-overlay]'))
    const content = overlay?.querySelector('[data-qv-content]')
    if (!overlay || !content) return

    lastFocus = /** @type {HTMLElement} */ (document.activeElement)
    content.innerHTML = '<div class="qv__info"><p class="qv__desc">…</p></div>'
    openOverlay(overlay)

    fetchSection(handle, 'hippo-quick-view').then((html) => {
      content.innerHTML = html
      resolveVariant(content)
      const close = overlay.querySelector('[data-qv-close]')
      if (close instanceof HTMLElement) close.focus()
    })
  }

  /* ------------------------------------------------------------------ *
   * Variant Picker
   * ------------------------------------------------------------------ */

  /** @param {string} handle */
  function openVariantPicker(handle) {
    const overlay = /** @type {HTMLElement | null} */ (document.querySelector('[data-vp-overlay]'))
    const content = overlay?.querySelector('[data-vp-content]')
    if (!overlay || !content) return

    lastFocus = /** @type {HTMLElement} */ (document.activeElement)
    content.innerHTML =
      '<div class="vp__head"><div class="vp__head-main"><h3 class="vp__title">…</h3></div></div>'
    openOverlay(overlay)

    fetchSection(handle, 'hippo-variant-picker').then((html) => {
      content.innerHTML = html
      resolveVariant(content)
      const close = overlay.querySelector('[data-vp-close]')
      if (close instanceof HTMLElement) close.focus()
    })
  }

  /* ------------------------------------------------------------------ *
   * Delegated interaction
   * ------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target)
    if (!target) return

    // --- Card buttons -------------------------------------------------
    const quickView = target.closest('[data-quick-view]')
    if (quickView instanceof HTMLElement) {
      event.preventDefault()
      event.stopPropagation()
      openQuickView(quickView.dataset.quickView || '')
      return
    }

    const quickAdd = target.closest('[data-quick-add]')
    if (quickAdd instanceof HTMLButtonElement) {
      event.preventDefault()
      event.stopPropagation()
      if (quickAdd.disabled) return
      if (quickAdd.dataset.needsOptions === 'true') {
        openVariantPicker(quickAdd.dataset.productHandle || '')
      } else {
        addVariant(
          quickAdd.dataset.variantId || '',
          Number(quickAdd.dataset.quantity) || 1,
          quickAdd
        )
      }
      return
    }

    const buyNowBtn = target.closest('[data-buy-now]')
    if (buyNowBtn instanceof HTMLButtonElement) {
      event.preventDefault()
      event.stopPropagation()
      if (buyNowBtn.disabled) return
      if (buyNowBtn.dataset.needsOptions === 'true') {
        openVariantPicker(buyNowBtn.dataset.productHandle || '')
      } else {
        buyNow(
          buyNowBtn.dataset.variantId || '',
          Number(buyNowBtn.dataset.quantity) || 1,
          buyNowBtn
        )
      }
      return
    }

    // --- Overlay dismissal -------------------------------------------
    if (target.closest('[data-qv-close]')) {
      const ov = target.closest('[data-qv-overlay]')
      if (ov instanceof HTMLElement) closeOverlay(ov)
      return
    }
    if (target.closest('[data-vp-close]')) {
      const ov = target.closest('[data-vp-overlay]')
      if (ov instanceof HTMLElement) closeOverlay(ov)
      return
    }
    // scrim (click directly on the overlay backdrop)
    if (target.matches('[data-qv-overlay], [data-vp-overlay]')) {
      closeOverlay(target)
      return
    }

    // --- Option selection (swatch / pill) ----------------------------
    const opt = target.closest('[data-option-position]')
    if (opt instanceof HTMLElement) {
      const field = opt.closest('[data-option-field]')
      const content = opt.closest('[data-qv-content], [data-vp-content]')
      if (field && content) {
        field
          .querySelectorAll('[data-option-position]')
          .forEach((b) => b.setAttribute('aria-pressed', 'false'))
        opt.setAttribute('aria-pressed', 'true')
        const label = field.querySelector('[data-option-label]')
        if (label) label.textContent = opt.dataset.value || ''
        resolveVariant(content)
      }
      return
    }

    // --- Quantity stepper --------------------------------------------
    const step = target.closest('[data-step]')
    if (step instanceof HTMLElement) {
      const stepper = step.closest('[data-vp-stepper]')
      const qtyEl = stepper?.querySelector('[data-vp-qty]')
      if (stepper && qtyEl) {
        const min = Number(stepper.getAttribute('data-min')) || 1
        const current = Number(qtyEl.textContent) || min
        const next = Math.max(min, Math.min(99, current + Number(step.dataset.step)))
        qtyEl.textContent = String(next)
      }
      return
    }

    // --- Quick View add ----------------------------------------------
    const qvAtc = target.closest('[data-qv-atc]')
    if (qvAtc instanceof HTMLButtonElement && !qvAtc.disabled) {
      const overlay = qvAtc.closest('[data-qv-overlay]')
      addVariant(qvAtc.dataset.variantId || '', 1, qvAtc).then((ok) => {
        if (ok && overlay instanceof HTMLElement) closeOverlay(overlay)
      })
      return
    }

    // --- Variant Picker buy now --------------------------------------
    const vpBuy = target.closest('[data-vp-buy]')
    if (vpBuy instanceof HTMLButtonElement) {
      if (!vpBuy.disabled) {
        const qtyEl = vpBuy.closest('.vp')?.querySelector('[data-vp-qty]')
        buyNow(vpBuy.dataset.variantId || '', Number(qtyEl?.textContent) || 1, vpBuy)
      }
      return
    }

    // --- Variant Picker add ------------------------------------------
    const vpAtc = target.closest('[data-vp-atc]')
    if (vpAtc instanceof HTMLButtonElement && !vpAtc.disabled) {
      const overlay = vpAtc.closest('[data-vp-overlay]')
      const qtyEl = vpAtc.closest('.vp')?.querySelector('[data-vp-qty]')
      const qty = Number(qtyEl?.textContent) || 1
      addVariant(vpAtc.dataset.variantId || '', qty, vpAtc).then((ok) => {
        if (!ok) return
        vpAtc.classList.add('is-done')
        const label = vpAtc.querySelector('[data-vp-atc-label]')
        const addedLabel = window.theme?.strings?.addedToCart
        if (label && addedLabel) label.textContent = addedLabel
        setTimeout(() => {
          if (overlay instanceof HTMLElement) closeOverlay(overlay)
        }, PICKER_CLOSE_MS)
      })
    }
  })

  // Esc to close + simple focus trap while an overlay is open.
  document.addEventListener('keydown', (event) => {
    const overlay = openOverlayEl()
    if (!overlay) return

    if (event.key === 'Escape') {
      closeOverlay(overlay)
      return
    }
    if (event.key !== 'Tab') return

    const focusables = /** @type {HTMLElement[]} */ (
      Array.from(
        overlay.querySelectorAll(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el instanceof HTMLElement && el.offsetParent !== null)
    )
    if (!focusables.length) return

    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  })

  /* ------------------------------------------------------------------ *
   * Header cart-count badge (Hippo header uses `.actions__count`, not the
   * legacy `.cart-link__bubble-num` that buildCart() updates).
   * ------------------------------------------------------------------ */
  document.addEventListener('ajaxProduct:added', syncCartCountBadge)

  // Page restored from bfcache (e.g. Back after checkout) — the server-rendered
  // `.actions__count` is stale, so resync it from /cart.js.
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return
    syncCartCountBadge()
    // Back from checkout after Buy Now: buttons were left in their loading state.
    document.querySelectorAll('[data-buy-now], [data-vp-buy]').forEach((btn) => {
      if (btn instanceof HTMLButtonElement) {
        btn.disabled = false
        btn.classList.remove('btn--loading')
      }
    })
  })

  /** Updates `.actions__count` in the Hippo header from /cart.js (creates/removes as needed). */
  function syncCartCountBadge() {
    const cartUrl = window.theme?.routes?.cart || '/cart.js'
    fetch(cartUrl, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        const cartLink = document.querySelector('.actions__cart')
        if (!cartLink) return
        let badge = cartLink.querySelector('.actions__count')
        if (cart.item_count > 0) {
          if (!badge) {
            badge = document.createElement('span')
            badge.className = 'actions__count'
            cartLink.appendChild(badge)
          }
          badge.textContent = cart.item_count
        } else if (badge) {
          badge.remove()
        }
      })
      .catch(() => {})
  }
})()
